import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
	AlertComponent,
	FormGroupComponent,
	ISelect,
	ModalComponent,
	SelectComponent,
} from '@tecalisdev/infinity';
import { environment } from '../../../../environments/environment';
import { LoadingService } from '../../../shared/services/loading.service';
import { ReactiveFormsService } from '../../../shared/services/reactive-forms.service';
import { SnackbarService } from '../../../shared/services/snackbar.service';
import { IWorkflowsListBody } from '../../models/workflows.models';
import { WorkflowsService } from '../../services/workflows.service';

export type WorkflowDuplicatePeerEnv = 'int' | 'pre' | 'pro';
export type WorkflowDuplicateTargetEnv = 'current' | WorkflowDuplicatePeerEnv;

@Component({
	selector: 'app-modal-workflow-duplicate-to-env',
	imports: [
		ReactiveFormsModule,
		TranslatePipe,
		ModalComponent,
		AlertComponent,
		FormGroupComponent,
		SelectComponent,
	],
	templateUrl: './modal-workflow-duplicate-to-env.component.html',
	styleUrl: './modal-workflow-duplicate-to-env.component.scss',
})
export class ModalWorkflowDuplicateToEnvComponent implements OnInit {
	@Input({ required: true }) workflow!: IWorkflowsListBody;
	@Output() closeEvent = new EventEmitter<boolean>();

	private workflowsService = inject(WorkflowsService);
	private loading = inject(LoadingService);
	private trans = inject(TranslateService);
	private snackbarService = inject(SnackbarService);
	public reactiveFormService = inject(ReactiveFormsService);

	public environmentOptions: ISelect[] = [];
	public errorMsg = '';

	public form = new FormGroup({
		target_env: new FormControl<ISelect | string | null>(null, Validators.required),
	});

	ngOnInit(): void {
		this.environmentOptions = this.buildEnvironmentOptions();
	}

	/**
	 * current + INT/PRE/PRO, sin repetir el entorno en el que ya estás.
	 * Local: 4 opciones. INT/PRE/PRO: 3 (current sustituye al peer actual).
	 */
	private buildEnvironmentOptions(): ISelect[] {
		const hosting = this.resolveHostingEnv();
		const peers: Array<{ id: WorkflowDuplicatePeerEnv; key: string }> = [
			{ id: 'int', key: 'WORKFLOWS.DUPLICATE_TO_ENV.ENV_INT' },
			{ id: 'pre', key: 'WORKFLOWS.DUPLICATE_TO_ENV.ENV_PRE' },
			{ id: 'pro', key: 'WORKFLOWS.DUPLICATE_TO_ENV.ENV_PRO' },
		];

		const options: ISelect[] = [
			{
				id: 'current',
				label: this.trans.instant('WORKFLOWS.DUPLICATE_TO_ENV.ENV_CURRENT'),
			},
		];

		for (const peer of peers) {
			if (peer.id === hosting) continue;
			options.push({
				id: peer.id,
				label: this.trans.instant(peer.key),
			});
		}

		return options;
	}

	/** Peer en el que corre el front; `null` en local. */
	private resolveHostingEnv(): WorkflowDuplicatePeerEnv | null {
		const url = (environment.publicUrl ?? '').toLowerCase();
		if (url.includes('pre.')) {
			return 'pre';
		}
		if (url.includes('pro.') || environment.production) {
			return 'pro';
		}
		if (url.includes('int.')) {
			return 'int';
		}
		return null;
	}

	public closeModal(type: string): void {
		const actions: Record<string, () => void> = {
			buttonPrimary: () => {
				if (this.form.invalid) {
					this.form.markAllAsTouched();
					return;
				}
				this.submit();
			},
		};

		actions[type] ? actions[type]() : this.closeEvent.emit(false);
	}

	private submit(): void {
		this.errorMsg = '';
		const raw = this.form.getRawValue().target_env;
		const targetEnv = String(
			raw && typeof raw === 'object' && 'id' in raw ? (raw as ISelect).id : raw,
		) as WorkflowDuplicateTargetEnv;

		this.loading.show(this.trans.instant('LOADING.DUPLICATE_WORKFLOWS'));
		this.workflowsService
			.duplicateToEnv({
				profile_uuid: this.workflow.uuid,
				target_env: targetEnv,
			})
			.subscribe({
				next: () => {
					this.snackbarService.show(this.trans.instant('WORKFLOWS.DUPLICATE_TO_ENV.SUCCESS'));
					this.closeEvent.emit(true);
				},
				error: (error: HttpErrorResponse) => {
					this.errorMsg =
						error.error?.message || this.trans.instant('WORKFLOWS.DUPLICATE_TO_ENV.ERROR');
					this.loading.hide();
				},
				complete: () => {
					this.loading.hide();
				},
			});
	}
}
